//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { getNgRedux } from '@react-bridge/redux-bridge';
import { usePostMessageListener } from '@uia/rights/hooks/usePostMessageListener';
import { getIsNonAtMentionedRightsRequestsDisplayApiEnabled } from '@uia/rights/selectors/getUIARights';
import {
  OAuthLandingPageMessageType,
  OAuthLandingPagePostMessage,
  RequestRightsOAuthParams,
  getInstagramOAuthUrl,
  isOAuthMessage,
} from '@uia/rights/utils/OAuthPostMessageUtils';
import { setRightsStatusChanged } from '@uia/rights-request/rights-request-actions';

import { RIGHTS_STATUS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';

/**
 * A custom hook that manages OAuth rights request authorization.
 *
 * This hook listens for post messages related to OAuth authorization processes,
 * handling checks such as successful completion, cancellation, and failure.
 *
 * @returns {function authorize(RequestRightsOAuthParams): boolean | undefined} A function that initiates
 * an OAuth flow for requesting rights through Instagram or navigates to a rights request OAuth landing
 * page. The function takes parameters that are included in the OAuth request/redirect, and returns
 * the current resolution state as a boolean indicator or undefined while awaiting a response.
 *
 * @param {RequestRightsOAuthParams} params - The parameters for rights request OAuth,
 * used to build the OAuth URL.
 */
export const useRequestRightsInAuthorizedIgUrl = () => {
  const isOAuthUrlEnabled = useSelector(
    getIsNonAtMentionedRightsRequestsDisplayApiEnabled,
  );

  const queryClient = useQueryClient();

  usePostMessageListener<OAuthLandingPagePostMessage>({
    filter: (message) => (isOAuthMessage(message.type) ? message : undefined),
    listener: async (data: OAuthLandingPagePostMessage) => {
      if (data.type === OAuthLandingPageMessageType.OAuthCheckPassed) {
        await queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.UniversalImageActions,
            QueryKeyConstants.UniversalImageActionsMetadata,
          ],
        });

        /**
         * We are unable to use Redux or the Angular injector due to the multiple Angular context issue.
         * TODO: PD-261610 - Migrate the UIA modal to React and remove the shared ngReduxInstance.
         */
        getNgRedux().dispatch(setRightsStatusChanged(RIGHTS_STATUS.INITIATED));
      }
    },
  });

  return (params: RequestRightsOAuthParams) => {
    // This will be obviated once we no longer feature flag checking which account the user is oauthed with
    const state = encodeURIComponent(JSON.stringify(params));

    // Start an implicit OAuth flow to instagram, redirecting to our
    // rights request oauth landing after to continue our user-account
    // checking logic.
    window.open(
      isOAuthUrlEnabled
        ? getInstagramOAuthUrl(params)
        : `${location.origin}/rights-request-oauth-landing?state=${state}`,
      'targetWindow',
      'toolbar = no, location = no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=750,height=690,left=200,top=100',
    );
  };
};
